import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api-client";

/**
 * Read-only hook for the tenant's lookup tables, used by the field
 * editor's LookupConfigSection (WU-5) and any future read-side
 * consumers (rule editor, validation surfaces).
 *
 * Wraps `GET /api/lookup-tables` — no new API surface; the route
 * already returns each table with a `columns: string[]` schema, which
 * is what the editor needs to populate display/return column
 * dropdowns without a second fetch per table.
 *
 * If you need write semantics (create/update/delete), the lookup
 * tables admin owns those flows directly — this hook is a read
 * helper, not a CRUD facade.
 */

export interface LookupTableSummary {
  id: string;
  tenantId: string;
  name: string;
  /** Column names known by the table (first-row CSV headers, etc.). */
  columns: string[];
  /** Number of rows in the table — useful for empty-state hinting. */
  entryCount: number;
  createdAt: string;
  updatedAt: string;
}

interface UseLookupTablesReturn {
  tables: LookupTableSummary[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useLookupTables(): UseLookupTablesReturn {
  const [tables, setTables] = useState<LookupTableSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<LookupTableSummary[]>("/lookup-tables");
      setTables(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { tables, isLoading, error, refresh };
}
